import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import API from '../utils/apiconfig';
import { isValidMobileOptional, parseApiErrors } from '../utils/formErrors';
import { useAuth } from '../context/Authcontext';
import { useGlobalError } from '../context/GlobalErrorContext';
import { getRankDisplay, rankTheme } from '../utils/rankBadge';
import { usePasswordVisibility, PasswordVisibilityToggle, PASSWORD_INPUT_CLASS } from '../components/PasswordVisibilityToggle';

const RANK_ICONS = { bronze: '🥉', silver: '🥈', gold: '🥇', platinum: '💎', diamond: '✨' };
const RANK_COLORS = {
  bronze:   'bg-orange-50 text-orange-700 border-orange-200',
  silver:   'bg-slate-100 text-slate-600 border-slate-300',
  gold:     'bg-amber-50 text-amber-700 border-amber-200',
  platinum: 'bg-cyan-50 text-cyan-700 border-cyan-200',
  diamond:  'bg-violet-50 text-violet-700 border-violet-200',
};
const SECTIONS = [
  { id: 'account', label: 'Account', icon: '👤' },
  { id: 'payout',  label: 'Payout',  icon: '💳' },
  { id: 'security', label: 'Security', icon: '🔒' },
];

function GlassPanel({ children, className = '' }) {
  return (
    <div
      className={`rounded-3xl border border-white/90 bg-white/80 shadow-[0_25px_70px_-20px_rgba(79,70,229,0.14),0_8px_32px_-12px_rgba(15,23,42,0.08)] backdrop-blur-xl ring-1 ring-indigo-950/[0.04] ${className}`}
    >
      {children}
    </div>
  );
}

function IconPhone({ className = 'h-4 w-4' }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.65} stroke="currentColor" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
    </svg>
  );
}

function IconBank({ className = 'h-4 w-4' }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.65} stroke="currentColor" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M5 10V19M9 10V19M15 10V19M19 10V19M4 19h16M12 3l8 5H4l8-5z" />
    </svg>
  );
}

function IconLock({ className = 'h-4 w-4' }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.65} stroke="currentColor" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
    </svg>
  );
}

function IconLink({ className = 'h-4 w-4' }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.65} stroke="currentColor" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
    </svg>
  );
}

function InputField({ label, value, onChange, placeholder, type = 'text', icon: Icon, required = false, error, hint, disabled = false }) {
  const isPassword = type === 'password';
  const { visible, toggle, inputType } = usePasswordVisibility();

  return (
    <div>
      <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-[0.16em] mb-1.5">
        {label}
        {required && <span className="text-red-500 ml-0.5" aria-hidden>*</span>}
        {hint && !required ? (
          <span className="ml-1 font-normal normal-case tracking-normal text-slate-400">({hint})</span>
        ) : null}
      </label>
      <div className="relative">
        {Icon && (
          <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400">
            <Icon className="h-4 w-4" />
          </span>
        )}
        <input
          type={isPassword ? inputType : type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          aria-invalid={error ? 'true' : undefined}
          className={`w-full py-2.5 border rounded-xl bg-white text-slate-800 text-sm outline-none transition placeholder-slate-300 disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed ${
            error
              ? 'border-rose-300 focus:ring-2 focus:ring-rose-200/80 focus:border-rose-400'
              : 'border-slate-200/90 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400'
          } ${Icon ? 'pl-10' : 'pl-3.5'} ${isPassword ? `pr-10 ${PASSWORD_INPUT_CLASS}` : 'pr-3.5'}`}
        />
        {isPassword ? <PasswordVisibilityToggle visible={visible} onToggle={toggle} /> : null}
      </div>
      {error ? (
        <p className="mt-1 text-xs font-medium text-rose-600" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function Alert({ type, children }) {
  return (
    <div
      className={`p-3.5 rounded-xl text-xs font-medium flex items-start gap-2 ${
        type === 'error'
          ? 'bg-red-50 border border-red-200 text-red-700'
          : 'bg-emerald-50 border border-emerald-200 text-emerald-700'
      }`}
    >
      <span className="text-base leading-none">{type === 'error' ? '⚠️' : '✅'}</span>
      <span>{children}</span>
    </div>
  );
}

function Profile() {
  const { user, updateUser } = useAuth();
  const { reportError } = useGlobalError();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    phone: '',
    bank_account_number: '',
    bank_ifsc: '',
    bank_name: '',
    upi_id: '',
    account_holder_name_as_per_bank: '',
  });
  const [pwForm, setPwForm] = useState({ old_password: '', new_password: '', confirm_password: '' });
  const [saving, setSaving] = useState(false);
  const [pwSave, setPwSave] = useState(false);
  const [success, setSuccess] = useState('');
  const [pwMsg, setPwMsg] = useState('');
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [pwErr, setPwErr] = useState('');
  const [activeSection, setActiveSection] = useState('account');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!user?.id) {
      navigate('/login');
      return;
    }
    let cancelled = false;
    api.get(API.PROFILE).then((r) => {
      if (cancelled) return;
      updateUser(r.data);
      setForm({
        phone:                           r.data.phone                           || '',
        bank_account_number:             r.data.bank_account_number             || '',
        bank_ifsc:                       r.data.bank_ifsc                       || '',
        bank_name:                       r.data.bank_name                       || '',
        upi_id:                          r.data.upi_id                          || '',
        account_holder_name_as_per_bank: r.data.account_holder_name_as_per_bank || '',
      });
    }).catch((err) => {
      if (!cancelled) reportError(err);
    });
    return () => { cancelled = true; };
  }, [user?.id, navigate, updateUser, reportError]); // fetch once per user id; stable helpers from context

  const setF = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setFieldErrors((prev) => {
      if (!prev[k]) return prev;
      const next = { ...prev };
      delete next[k];
      return next;
    });
  };
  const setPF = (k, v) => setPwForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!success) return undefined;
    const timer = setTimeout(() => setSuccess(''), 2000);
    return () => clearTimeout(timer);
  }, [success]);

  const handleSaveAccount = async () => {
    setFieldErrors({});
    if (!form.phone?.trim()) {
      setFieldErrors({ phone: 'Mobile number is required.' });
      setError('Mobile number is required.');
      setSuccess('');
      return;
    }
    if (!isValidMobileOptional(form.phone)) {
      setFieldErrors({ phone: 'Enter a valid 10-digit mobile number.' });
      setError('Enter a valid 10-digit mobile number.');
      setSuccess('');
      return;
    }
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const res = await api.patch(API.PROFILE_UPDATE, { phone: form.phone.trim() });
      updateUser(res.data);
      setSuccess('Profile updated successfully!');
    } catch (e) {
      const data = e.response?.data;
      if (data && typeof data === 'object') {
        const { form: formMsg, fields } = parseApiErrors(data);
        setFieldErrors(fields);
        setError(formMsg || 'Save failed.');
      } else {
        setError('Save failed.');
      }
    }
    setSaving(false);
  };

  const payoutLocked = Boolean(user?.bank_details_locked);

  const handleSavePayout = async () => {
    if (payoutLocked) {
      setError(
        user?.bank_details_lock_message
          || 'Payout is in progress. Bank / UPI details are locked until admin approves a change.'
      );
      setSuccess('');
      return;
    }
    const requiredFields = [
      { key: 'account_holder_name_as_per_bank', label: 'Account holder name' },
      { key: 'bank_account_number', label: 'Bank account number' },
      { key: 'bank_ifsc', label: 'IFSC code' },
      { key: 'bank_name', label: 'Bank name' },
    ];
    const missing = requiredFields.filter(({ key }) => !form[key]?.trim()).map(({ label }) => label);
    if (missing.length) {
      setError(`Please fill required fields: ${missing.join(', ')}.`);
      setSuccess('');
      return;
    }
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const payload = {
        account_holder_name_as_per_bank: form.account_holder_name_as_per_bank.trim(),
        bank_account_number: form.bank_account_number.trim(),
        bank_ifsc: form.bank_ifsc.trim(),
        bank_name: form.bank_name.trim(),
        upi_id: form.upi_id?.trim() || '',
      };
      const res = await api.patch(API.PROFILE_UPDATE, payload);
      if (res.data?.success === false) {
        setError(res.data?.message || 'Could not save payout details.');
        return;
      }
      updateUser(res.data);
      setSuccess(res.data?.message || 'Payout details saved successfully!');
    } catch (e) {
      const data = e.response?.data;
      setError(
        data?.message
          || (data ? Object.values(data).flat().join(' ') : null)
          || 'Save failed.'
      );
    }
    setSaving(false);
  };

  const handleChangePassword = async () => {
    setPwErr('');
    setPwMsg('');
    if (!pwForm.old_password)                            { setPwErr('Enter your current password.');    return; }
    if (pwForm.new_password.length < 8)                  { setPwErr('New password must be 8+ chars.'); return; }
    if (pwForm.new_password !== pwForm.confirm_password) { setPwErr('Passwords do not match.');        return; }
    setPwSave(true);
    try {
      await api.post(API.CHANGE_PASSWORD, pwForm);
      setPwMsg('Password changed successfully!');
      setPwForm({ old_password: '', new_password: '', confirm_password: '' });
    } catch (e) {
      setPwErr(e.response?.data?.error || 'Change failed.');
    }
    setPwSave(false);
  };

  const handleCopyReferral = () => {
    navigator.clipboard.writeText(user?.referral_code || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const rankBadge = getRankDisplay(user);
  const { colorClass: rankColor, icon: rankIcon } = rankTheme(
    rankBadge.themeKey,
    RANK_COLORS,
    RANK_ICONS
  );
  const rewardPoints = user?.reward_points ?? 0;
  const lifetimePoints = user?.total_earned_points ?? 0;
  const completionPercent = Number(user?.profile_completion_percent ?? 0);
  const hasMobileDone = Boolean(user?.profile_completion_mobile);
  const hasBankDone = Boolean(user?.profile_completion_bank);
  const completionHint = (() => {
    if (completionPercent >= 100) return 'All basic details are complete.';
    const missing = [];
    if (!hasMobileDone) missing.push('mobile number');
    if (!hasBankDone) missing.push('bank / UPI details');
    return `Add ${missing.join(' and ')} to reach 100%.`;
  })();

  return (
    <div className="relative min-h-screen overflow-x-hidden bg-gradient-to-br from-slate-50 via-indigo-50/40 to-cyan-50/50 text-slate-800">
      {/* Ambient background */}
      <div className="pointer-events-none fixed inset-0 bg-gradient-to-b from-white/70 via-transparent to-cyan-50/30" aria-hidden />
      <div
        className="pointer-events-none fixed inset-0 opacity-90"
        style={{
          background:
            'radial-gradient(ellipse 80% 45% at 50% -5%, rgba(165,180,252,0.35), transparent 55%), radial-gradient(ellipse 40% 35% at 100% 10%, rgba(207,250,254,0.65), transparent 45%), radial-gradient(ellipse 35% 30% at 0% 40%, rgba(199,210,254,0.4), transparent 42%)',
        }}
        aria-hidden
      />

      <div className="relative z-10 w-full min-h-[calc(100vh-4rem)] px-4 py-6 sm:px-5 sm:py-8">
        <div className="max-w-5xl mx-auto space-y-5 sm:space-y-6">

          {/* Page header */}
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div className="text-center sm:text-left">
              <p className="text-[11px] font-bold tracking-[0.22em] text-indigo-600 uppercase mb-2">Account</p>
              <h1
                className="text-2xl sm:text-3xl font-black text-slate-900 leading-tight"
                style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}
              >
                My Profile
              </h1>
              <p className="text-sm text-slate-600 mt-1.5 max-w-xl">
                Manage your account details, referral link, and payout information in one place.
              </p>
              <div className="mt-4 h-1 w-16 rounded-full bg-gradient-to-r from-indigo-500 via-cyan-500 to-violet-500 shadow-[0_4px_20px_rgba(99,102,241,0.25)] mx-auto sm:mx-0" />
            </div>

            <div className="mx-auto sm:mx-0 w-full max-w-[11.5rem] rounded-2xl border border-indigo-100 bg-white/90 px-3 py-3 shadow-sm">
              <div className="flex items-center gap-3">
                <div
                  className="relative h-14 w-14 flex-shrink-0"
                  role="img"
                  aria-label={`Profile ${completionPercent}% complete`}
                >
                  <svg className="h-14 w-14 -rotate-90" viewBox="0 0 36 36" aria-hidden>
                    <circle cx="18" cy="18" r="15.5" fill="none" stroke="#e2e8f0" strokeWidth="3" />
                    <circle
                      cx="18"
                      cy="18"
                      r="15.5"
                      fill="none"
                      stroke={completionPercent >= 100 ? '#059669' : '#4f46e5'}
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeDasharray={`${completionPercent} ${100 - completionPercent}`}
                      pathLength="100"
                    />
                  </svg>
                  <span className="absolute inset-0 flex items-center justify-center text-sm font-black text-slate-900 tabular-nums">
                    {completionPercent}%
                  </span>
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Profile</p>
                  <p className="text-xs font-semibold text-slate-800 leading-snug">{completionHint}</p>
                </div>
              </div>
              <div className="mt-2.5 space-y-1 text-[11px]">
                <p className={hasMobileDone ? 'text-emerald-700 font-semibold' : 'text-slate-500'}>
                  {hasMobileDone ? '✓' : '○'} Mobile (50%)
                </p>
                <p className={hasBankDone ? 'text-emerald-700 font-semibold' : 'text-slate-500'}>
                  {hasBankDone ? '✓' : '○'} Bank / UPI (50%)
                </p>
              </div>
            </div>
          </div>

          {/* Hero identity card */}
          <GlassPanel className="p-5 sm:p-6">
            <div className="rounded-2xl border border-slate-200/80 bg-slate-50/60 p-4 sm:p-5">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-5">
                <div
                  className="w-16 h-16 sm:w-[4.5rem] sm:h-[4.5rem] rounded-2xl bg-slate-900 flex items-center justify-center text-white text-2xl sm:text-3xl font-black flex-shrink-0 shadow-sm ring-1 ring-slate-900/10"
                  style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}
                >
                  {user?.username?.[0]?.toUpperCase()}
                </div>

                <div className="flex-1 min-w-0 text-center sm:text-left">
                  <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 mb-1">
                    <h2
                      className="text-xl sm:text-2xl font-black text-slate-900"
                      style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}
                    >
                      {user?.username}
                    </h2>
                    {rankBadge.show && (
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border capitalize ${rankColor}`}>
                        {rankIcon} {rankBadge.label}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-500">{user?.email}</p>
                  {user?.referred_by_username && (
                    <p className="text-xs text-slate-400 mt-1">
                      Referred by{' '}
                      <span className="font-medium text-slate-700">{user.referred_by_username}</span>
                    </p>
                  )}
                </div>
              </div>

              {/* Stats row */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4 pt-4 border-t border-slate-200/70">
                <div className="rounded-xl border border-slate-200 bg-white p-3 sm:p-3.5">
                  <p className="text-xs text-slate-500">Available points</p>
                  <p className="text-lg sm:text-xl font-black text-slate-900 mt-0.5 tabular-nums">
                    {rewardPoints.toLocaleString('en-IN')}
                  </p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-white p-3 sm:p-3.5">
                  <p className="text-xs text-slate-500">Lifetime earned</p>
                  <p className="text-lg sm:text-xl font-black text-slate-900 mt-0.5 tabular-nums">
                    {lifetimePoints.toLocaleString('en-IN')}
                  </p>
                </div>
                <div className="col-span-2 sm:col-span-1 rounded-xl border border-slate-200 bg-white p-3 sm:p-3.5 flex items-center gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-base border border-slate-200">
                    {rankBadge.show ? rankIcon : '—'}
                  </span>
                  <div>
                    <p className="text-xs text-slate-500">Member rank</p>
                    <p className="text-lg font-black text-slate-900 capitalize leading-tight">
                      {rankBadge.show ? rankBadge.label : 'Not ranked'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </GlassPanel>

          {/* Referral */}
          <GlassPanel className="p-5 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <p className="text-[11px] font-bold tracking-[0.18em] text-indigo-600 uppercase">Referral</p>
                <p className="text-sm text-slate-500 mt-1">Share your link to grow your network and earn rewards</p>
              </div>
            </div>

            <div className="mt-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <div className="flex-1 flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50/80 px-4 py-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-indigo-100 text-indigo-600">
                  <IconLink />
                </span>
                <span className="font-mono text-lg sm:text-xl font-black text-slate-900 tracking-wide truncate">
                  {user?.referral_code}
                </span>
              </div>
              <button
                type="button"
                onClick={handleCopyReferral}
                className={`shrink-0 rounded-xl px-5 py-3 text-sm font-extrabold transition-all ${
                  copied
                    ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-300/50'
                    : 'bg-gradient-to-r from-violet-500 to-indigo-500 text-white shadow-lg shadow-indigo-300/60 hover:from-violet-600 hover:to-indigo-600'
                }`}
              >
                {copied ? '✓ Copied!' : 'Copy referral link'}
              </button>
            </div>
          </GlassPanel>

          {/* Section tabs */}
          <div className="flex flex-wrap gap-2">
            {SECTIONS.map(({ id, label, icon }) => (
              <button
                key={id}
                type="button"
                onClick={() => setActiveSection(id)}
                className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold transition-all ${
                  activeSection === id
                    ? 'bg-gradient-to-r from-violet-500 to-indigo-500 text-white shadow-md shadow-indigo-300/50'
                    : 'bg-white/80 text-slate-600 border border-slate-200 hover:border-indigo-300 hover:text-indigo-600'
                }`}
              >
                <span aria-hidden>{icon}</span>
                {label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {/* Main form area */}
            <div className="lg:col-span-2 space-y-5">

              {/* Account section */}
              {activeSection === 'account' && (
                <GlassPanel className="p-5 sm:p-6 space-y-5">
                  <div>
                    <p className="text-[11px] font-bold tracking-[0.18em] text-indigo-600 uppercase">Personal information</p>
                    <p className="text-sm text-slate-500 mt-1">Your contact details for account communication</p>
                  </div>

                  <InputField
                    label="Mobile number"
                    value={form.phone}
                    onChange={(e) => setF('phone', e.target.value)}
                    placeholder="10-digit mobile number"
                    icon={IconPhone}
                    required
                    hint="unique per account"
                    error={fieldErrors.phone}
                  />

                  {error && <Alert type="error">{error}</Alert>}
                  {success && <Alert type="success">{success}</Alert>}

                  <button
                    type="button"
                    onClick={handleSaveAccount}
                    disabled={saving}
                    className="w-full sm:w-auto rounded-xl px-6 py-3 text-sm font-extrabold text-white bg-gradient-to-r from-violet-500 to-indigo-500 shadow-lg shadow-indigo-300/60 hover:from-violet-600 hover:to-indigo-600 disabled:opacity-50 transition-all"
                  >
                    {saving ? 'Saving…' : 'Save changes'}
                  </button>
                </GlassPanel>
              )}

              {/* Payout section */}
              {activeSection === 'payout' && (
                <GlassPanel className="p-5 sm:p-6 space-y-5">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="text-[11px] font-bold tracking-[0.18em] text-indigo-600 uppercase">Payout details</p>
                      <p className="text-sm text-slate-500 mt-1">
                        Used when admin processes your payout. Keep these up to date for faster processing.
                      </p>
                    </div>
                    <Link
                      to="/withdrawal"
                      className="inline-flex items-center gap-1.5 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-bold text-emerald-900 no-underline hover:bg-emerald-100 transition-colors"
                    >
                      View withdrawals →
                    </Link>
                  </div>

                  {payoutLocked ? (
                    <Alert type="error">
                      {user?.bank_details_lock_message
                        || 'Admin is paying you. Bank / UPI cannot be changed now. Ask admin if you need a change.'}
                    </Alert>
                  ) : user?.payout_needs_bank_details ? (
                    <Alert type="error">
                      Admin is ready to pay you. Please save UPI or bank details below, then tap Save.
                    </Alert>
                  ) : null}

                  <InputField
                    label="UPI ID"
                    value={form.upi_id}
                    onChange={(e) => setF('upi_id', e.target.value)}
                    placeholder="yourname@upi"
                    icon={IconBank}
                    disabled={payoutLocked}
                  />

                  <div className="h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />

                  <InputField
                    label="Account holder name as per bank account"
                    value={form.account_holder_name_as_per_bank}
                    onChange={(e) => setF('account_holder_name_as_per_bank', e.target.value)}
                    placeholder="Name as on bank records"
                    icon={IconBank}
                    required
                    disabled={payoutLocked}
                  />

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <InputField
                      label="Bank account no."
                      value={form.bank_account_number}
                      onChange={(e) => setF('bank_account_number', e.target.value)}
                      placeholder="Account number"
                      icon={IconBank}
                      required
                      disabled={payoutLocked}
                    />
                    <InputField
                      label="IFSC code"
                      value={form.bank_ifsc}
                      onChange={(e) => setF('bank_ifsc', e.target.value)}
                      placeholder="SBIN0001234"
                      required
                      disabled={payoutLocked}
                    />
                  </div>

                  <InputField
                    label="Bank name"
                    value={form.bank_name}
                    onChange={(e) => setF('bank_name', e.target.value)}
                    placeholder="State Bank of India"
                    required
                    disabled={payoutLocked}
                  />

                  {error && <Alert type="error">{error}</Alert>}
                  {success && <Alert type="success">{success}</Alert>}

                  <button
                    type="button"
                    onClick={handleSavePayout}
                    disabled={saving || payoutLocked}
                    className="w-full sm:w-auto rounded-xl px-6 py-3 text-sm font-extrabold text-white bg-gradient-to-r from-cyan-500 to-teal-500 shadow-lg shadow-cyan-300/60 hover:from-cyan-600 hover:to-teal-600 disabled:opacity-50 transition-all"
                  >
                    {saving ? 'Saving…' : payoutLocked ? 'Payout locked — contact admin' : 'Save payout details'}
                  </button>
                </GlassPanel>
              )}

              {/* Security section */}
              {activeSection === 'security' && (
                <GlassPanel className="p-5 sm:p-6 space-y-5">
                  <div>
                    <p className="text-[11px] font-bold tracking-[0.18em] text-indigo-600 uppercase">Change password</p>
                    <p className="text-sm text-slate-500 mt-1">Use a strong password you do not use elsewhere</p>
                  </div>

                  <InputField
                    label="Current password"
                    type="password"
                    value={pwForm.old_password}
                    onChange={(e) => setPF('old_password', e.target.value)}
                    placeholder="Your current password"
                    icon={IconLock}
                  />
                  <InputField
                    label="New password"
                    type="password"
                    value={pwForm.new_password}
                    onChange={(e) => setPF('new_password', e.target.value)}
                    placeholder="Min 8 characters"
                    icon={IconLock}
                  />
                  <InputField
                    label="Confirm password"
                    type="password"
                    value={pwForm.confirm_password}
                    onChange={(e) => setPF('confirm_password', e.target.value)}
                    placeholder="Repeat new password"
                    icon={IconLock}
                  />

                  {pwForm.new_password && (
                    <div className="rounded-2xl border border-slate-100 bg-slate-50/80 p-4">
                      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-3">Password strength</p>
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          { label: '8+ characters', ok: pwForm.new_password.length >= 8 },
                          { label: 'Has number', ok: /\d/.test(pwForm.new_password) },
                          { label: 'Has uppercase', ok: /[A-Z]/.test(pwForm.new_password) },
                          {
                            label: 'Passwords match',
                            ok: pwForm.new_password === pwForm.confirm_password && pwForm.confirm_password.length > 0,
                          },
                        ].map(({ label, ok }) => (
                          <div
                            key={label}
                            className={`text-xs flex items-center gap-2 font-medium ${ok ? 'text-emerald-600' : 'text-slate-400'}`}
                          >
                            <span
                              className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] ${
                                ok ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-400'
                              }`}
                            >
                              {ok ? '✓' : '○'}
                            </span>
                            {label}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {pwErr && <Alert type="error">{pwErr}</Alert>}
                  {pwMsg && <Alert type="success">{pwMsg}</Alert>}

                  <button
                    type="button"
                    onClick={handleChangePassword}
                    disabled={pwSave}
                    className="w-full sm:w-auto rounded-xl px-6 py-3 text-sm font-extrabold text-white bg-gradient-to-r from-slate-800 to-slate-900 shadow-lg shadow-slate-400/30 hover:from-slate-700 hover:to-slate-800 disabled:opacity-50 transition-all"
                  >
                    {pwSave ? 'Changing…' : 'Update password'}
                  </button>
                </GlassPanel>
              )}
            </div>

            {/* Sidebar tips */}
            <div className="space-y-4">
              <GlassPanel className="p-5">
                <p className="text-[11px] font-bold tracking-[0.18em] text-indigo-600 uppercase mb-3">Quick tips</p>
                <ul className="space-y-3 text-sm text-slate-600">
                  <li className="flex gap-2">
                    <span className="text-indigo-500 shrink-0">•</span>
                    <span>Keep your UPI and bank details current — admin-initiated payouts use these details.</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-indigo-500 shrink-0">•</span>
                    <span>Share your referral link to earn rewards as your network grows.</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-indigo-500 shrink-0">•</span>
                    <span>Use a unique password with at least 8 characters for account security.</span>
                  </li>
                </ul>
              </GlassPanel>

              <GlassPanel className="p-5 bg-gradient-to-br from-indigo-50/80 to-cyan-50/60">
                <p className="text-[11px] font-bold tracking-[0.18em] text-indigo-600 uppercase mb-2">Your rank</p>
                {user?.rank_status_notice?.message && (
                  <p className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5 mb-3 leading-relaxed">
                    {user.rank_status_notice.message}
                  </p>
                )}
                <p className="text-sm text-slate-600 leading-relaxed">
                  {rankBadge.show ? (
                    <>
                      You are currently at{' '}
                      <strong className="capitalize text-slate-800">{rankBadge.label}</strong> level.
                      Keep growing your active referred network to unlock higher tiers.
                    </>
                  ) : (
                    <>
                      You have not reached a network rank yet. Grow active referred IDs
                      (admin-approved payments) to unlock tiers
                      {user?.ids_to_next_tier != null && user?.next_tier?.label
                        ? ` — ${user.ids_to_next_tier} more active IDs to ${user.next_tier.label}`
                        : ' (Bronze starts at 50 active IDs)'}
                      .
                    </>
                  )}
                </p>
                <button
                  type="button"
                  onClick={() => navigate('/rewards')}
                  className="mt-4 w-full rounded-xl border border-indigo-200 bg-white/80 py-2.5 text-xs font-bold text-indigo-700 hover:bg-indigo-50 transition-colors"
                >
                  View reward milestones →
                </button>
              </GlassPanel>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

export default Profile;




